/**
 * @typedef {Object} LineItem
 * @property {String} title - The line item title
 * @property {String} description - The line item description
 * @property {Number} quantity - The line item quantity (default in base 100, e.g. 100 - 1.00)
 * @property {Object} price - The line item price object
 * @property {Number} price.amount - The amount of the line item
 * @property {String} price.currency - The currency of the amount
 * @property {String} vat_rate - The string of the vat rate
 * @property {Object} vat - The line item vat object
 * @property {Number} vat.amount - The vat amount of the line item
 * @property {String} vat.currency - The currency of the vat amount
 */
import { Job, JobItem } from '@kanda-libs/ks-frontend-services';

import { FormattedJob } from 'types';

/**
 * @typedef {Object} Totals
 * @property {Number} totalExcVat - The total excluding VAT
 * @property {Number} totalVat - The total VAT
 * @property {Number} totalIncVat - The total including VAT
 */

const calculateVatPercentage = (vat) => {
  // if (vat === 'exempted') return 0;
  if (!vat.includes('%')) return 0;
  try {
    const vatPercentage = parseInt(vat, 10);
    return vatPercentage;
  } catch (e) {
    return 0;
  }
};

/**
 * Calculates item total from parts
 *
 * @param {Int} quantity Quantity for item, in units e.g. 100 = 1.00 (default)
 * @param {Int} price Price for item, in pence
 * @param {String} vat Vat for item
 */
export const calculateItemTotal = (
  quantity = 0,
  price = 0,
  vat: JobItem['vat_rate'] = 'vat_not_set',
  vatAmount = 0,
  quantityDecimal = 100,
) => {
  // Get VAT percentage
  const vatPercentage = calculateVatPercentage(vat);
  // Calculate exc-VAT total
  const totalExcVat =
    // Quantity times price, over quantityDecimal
    (quantity * price) / quantityDecimal;

  if (vat === 'custom')
    return {
      totalExcVat,
      totalVat: vatAmount,
      totalIncVat: totalExcVat + vatAmount,
    };

  // returns if no VAT to add
  if (vatPercentage === null)
    return {
      totalExcVat,
      totalVat: 0,
      totalIncVat: totalExcVat,
    };
  // Calculate VAT total
  const totalVat = Math.ceil((totalExcVat * vatPercentage) / 100);
  // Calculate total (incl. VAT)
  const totalIncVat = Math.ceil(totalExcVat + totalVat);
  return {
    totalExcVat,
    totalVat,
    totalIncVat,
  };
};

/**
 * Calculates item total from parts
 *
 * @param {[LineItem]} lineItems Array of line items
 * @returns {Totals} totals object
 */
export const calculateJobTotal = (lineItems) => {
  if (!lineItems || lineItems?.length === 0)
    return {
      totalExcVat: 0,
      totalVat: 0,
      totalIncVat: 0,
    };
  return lineItems.reduce(
    (totals, item) => {
      const itemTotal = calculateItemTotal(
        item.quantity,
        item.price.amount,
        item.vat_rate,
        item.vat.amount,
      );
      return {
        totalExcVat: totals.totalExcVat + itemTotal.totalExcVat,
        totalVat: itemTotal.totalVat
          ? totals.totalVat + itemTotal.totalVat
          : totals.totalVat,
        totalIncVat: totals.totalIncVat + itemTotal.totalIncVat,
      };
    },
    {
      totalExcVat: 0,
      totalVat: 0,
      totalIncVat: 0,
    },
  );
};

export const formatLogo = (logo) => {
  if (!logo || !logo?.mimetype || !logo?.content || !logo?.name)
    return {
      logo: null,
      name: null,
    };
  const prefix = `data:${logo?.mimetype};base64,`;
  return {
    logo: `${prefix}${logo.content}`,
    name: logo?.name || 'logo',
  };
};

export const formatName = (customer) => {
  if (!customer?.first_name) return null;
  return ['For:', customer?.first_name, customer?.last_name]
    .filter((value) => value)
    .join(' ');
};

export const formatDate = (timestamp) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);

  const day = `0${date?.getDate()}`.slice(-2);
  const month = `0${date?.getMonth() + 1}`.slice(-2);
  const year = date?.getFullYear();

  return `${day}/${month}/${year}`;
};

export const getLatestLoanPayment = (job) => {
  const { payments } = job;

  if (!payments || payments?.length === 0) return null;

  const loanPayments = payments?.filter(
    (payment) => payment?.payment_option?.payment_method === 'loan',
  );

  return loanPayments[loanPayments?.length - 1];
};

export const isJobCustom = (job) => {
  if (!job) return false;
  const jobItems = job.job_items;

  if (jobItems.length > 1) {
    return false;
  }

  const { style } = jobItems[0];

  return style === 'custom';
};

export const getJobType = (job: Job | FormattedJob): JobItem['style'] => {
  if (!job) return undefined;
  const jobItems = job?.job_items;
  if (!jobItems || jobItems.length === 0) return undefined;
  const style = jobItems?.[0]?.style;
  return style;
};

export const hasPaidByCard = (job?: Job | FormattedJob): boolean => {
  if (!job) return false;
  const status = job?.status;
  const customerOption = job?.customer_options?.checkout_option;
  return status === 'accepted' && customerOption === 'pay_on_card';
};

export const hasPaidByBank = (job?: Job | FormattedJob): boolean => {
  if (!job) return false;
  const status = job?.status;
  const customerOption = job?.customer_options?.checkout_option;
  return (
    (status === 'accepted' || status === 'finished') &&
    customerOption === 'pay_by_bank'
  );
};

export const getJobDepositAmount = (job?: Job): number =>
  job?.deposit_value?.amount ?? 0;

export const getJobDepositType = (job?: Job): Job['deposit_type'] | undefined =>
  job?.deposit_type;

export const getJobAmountToPay = (job?: Job): number => {
  if (!job) return 0;
  const total = job.total?.amount_vat_inclusive?.amount ?? 0;
  const deposit = job.deposit_value.amount;
  return Math.max(0, total - deposit);
};
